import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { money, percent, modelNaOfercie } from '../format.ts'

interface Option {
  id: string
  optionId: string | null
  name: string
  price: string
  /** pieces of an add-on per machine; group options are always 1 */
  quantity: number
  optionGroup?: string | null
  /** set when the option picks one model out of a family — that model goes on the offer */
  variantModel?: string | null
  variantCode?: string | null
}
interface Item {
  id: string
  machineId: string
  model: string
  manufacturerName: string
  catalogCode: string
  quantity: number
  unitRetailPrice: string
  optionsTotal: string
  discountPct: string
  unitNetPrice: string
  lineNet: string
  vatRate: string
  discountLimitPctAtIssue: string
  requiresApproval: boolean
  options: Option[]
  marginPln?: string | null
  /** currency of the line's prices — 'EUR' for machines priced in EUR (ILMER) */
  currency: string
  /** lineNet in PLN with the offer's rate; null when NBP was unreachable */
  lineNetPln: number | null
}
interface Kurs {
  rate: string
  tableNo: string | null
  rateDate: string | null
  /** true once the offer is issued — the rate no longer moves */
  frozen: boolean
  /** draft only: NBP unreachable, the rate is our cached copy */
  fromCache?: boolean
  /** draft only: days since the table was published */
  ageDays?: number
}
interface Quote {
  id: string
  number: string | null
  status: string
  title: string | null
  /** what the offer is for, read off the first machine — printed when `title` is empty */
  proposedTitle: string | null
  currency: string
  /** present when any line is priced in EUR */
  kurs: Kurs | null
  netTotal: string | null
  vatTotal: string | null
  grossTotal: string | null
  approvalRequired: boolean
  approvalReason: string | null
  approvedAt: string | null
  isGrantOffer: boolean
  validityMonths: number
  issuedAt: string | null
  customer: { name: string; city: string | null; nip: string | null }
  items: Item[]
}
interface MachineHit {
  id: string
  model: string
  manufacturerName: string
  catalogCode: string
  retailPrice: string | null
  retailCurrency: string
  status?: string
}
interface CatalogOption {
  id: string
  name: string
  price: string | null
  currency: string
  defaultSelected: boolean
  /** Choice group ("Zbiornik główny", "Sterowanie") — pick one; null = add-on. */
  optionGroup: string | null
}
/** Same ceiling as the API (MAX_ILOSC_OPCJI). */
const MAX_OPTION_QTY = 9

/**
 * Writing an offer — SPEC 5.2 gives this whole flow under a minute, so every
 * interaction here is measured in keystrokes: the machine search is focused on
 * load, Enter adds, default extras arrive already ticked.
 */
export function QuoteEditorPage() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const { user } = useAuth()
  const [q, setQ] = useState<Quote | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const isOwner = user?.role === 'wlasciciel'

  function reload() {
    if (!id) return
    api
      .get<{ quote: Quote }>(`/api/quotes/${id}`)
      .then((r) => {
        setQ(r.quote)
        // A sent offer is a document, not a draft — bounce to the read view.
        if (r.quote.issuedAt) nav(`/oferty/${r.quote.id}`, { replace: true })
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  async function issue() {
    if (!q) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/quotes/${q.id}/issue`)
      nav(`/oferty/${q.id}`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się wystawić oferty')
      setBusy(false)
    }
  }

  async function requestApproval() {
    if (!q) return
    setBusy(true)
    try {
      await api.post(`/api/quotes/${q.id}/request-approval`)
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  /** An owner approves an over-limit discount right here — one click, logged (D1). */
  async function approve() {
    if (!q) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/quotes/${q.id}/approve`)
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się zaakceptować rabatu')
    } finally {
      setBusy(false)
    }
  }

  if (error && !q) return <div className="p-6 text-red-700">{error}</div>
  if (!q) return <div className="p-6 text-slate-400">Ładowanie…</div>

  const pusta = q.items.length === 0
  const czekaNaAkcept = q.approvalRequired && !q.approvedAt
  // The owner's estimate is only as good as the lines it knows the cost of.
  const bezMarzy = isOwner ? q.items.filter((i) => i.marginPln == null).length : 0
  const kursStary = q.kurs && !q.kurs.frozen && (q.kurs.fromCache || (q.kurs.ageDays ?? 0) > 3)

  return (
    <div className="p-6 max-w-5xl">
      <Link to="/oferty" className="text-sm text-slate-500 hover:text-slate-700">
        ← Oferty
      </Link>

      <div className="mt-2 mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">{pusta ? 'Nowa oferta' : 'Szkic oferty'}</h1>
          <div className="text-sm text-slate-500 mt-0.5">
            dla <strong className="text-slate-700">{q.customer.name}</strong>
            {q.customer.city && ` · ${q.customer.city}`}
            {q.isGrantOffer && (
              <span className="ml-2 bg-brand-100 text-brand-700 px-2 py-0.5 rounded text-xs">do wniosku o dotację</span>
            )}
          </div>
        </div>
      </div>

      <MachineSearch quoteId={q.id} onAdded={reload} />

      {!pusta && <TitleField quote={q} onChanged={reload} />}

      {q.items.length > 0 && (
        <div className="mt-4 space-y-3">
          {q.items.map((it) => (
            <ItemCard key={it.id} item={it} isOwner={isOwner} currency={q.currency} onChanged={reload} />
          ))}
        </div>
      )}

      {/* Totals + the send button */}
      <div className="mt-5 bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-6">
          <div className="text-sm space-y-1">
            <Row label="Razem netto" value={money(q.netTotal, q.currency)} strong />
            <Row label="VAT" value={money(q.vatTotal, q.currency)} />
            <Row label="Razem brutto" value={money(q.grossTotal, q.currency)} strong />
            {q.kurs && (
              <div className="text-[11px] text-slate-400">
                pozycje w EUR przeliczone po kursie NBP {Number(q.kurs.rate).toLocaleString('pl-PL', { minimumFractionDigits: 4 })} zł
                {q.kurs.rateDate ? ` z ${q.kurs.rateDate}` : ''}
                {q.kurs.frozen ? ' (zamrożony przy wystawieniu)' : ' — zamrozi się przy wystawieniu'}
              </div>
            )}
            {kursStary && (
              <div className="text-[11px] text-amber-700">
                Uwaga: NBP jest niedostępne, to kurs z pamięci{q.kurs?.ageDays ? ` sprzed ${q.kurs.ageDays} dni` : ''} — wystawienie zamrozi właśnie ten kurs.
              </div>
            )}
            {/* Draft — the margin here is always today's estimate; it freezes on send. */}
            {isOwner && q.items.some((i) => i.marginPln) && (
              <>
                <Row
                  label="Marża szacunkowa"
                  value={money(
                    q.items.reduce((a, i) => a + Number(i.marginPln ?? 0), 0),
                    q.currency,
                  )}
                  tone="green"
                />
                <div className="text-[11px] text-slate-400">
                  po dzisiejszym cenniku zakupu i kursie — zamrozi się przy wystawieniu
                  {bezMarzy > 0 && ` · bez ${bezMarzy} pozycji o nieznanej marży`}
                </div>
              </>
            )}
          </div>

          <div className="text-right">
            {czekaNaAkcept ? (
              <div>
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2 max-w-xs">
                  {isOwner
                    ? 'Rabat przekracza limit producenta. Zatwierdź go jako właściciel — dopiero potem można wystawić ofertę.'
                    : 'Rabat przekracza limit producenta. Oferta wymaga akceptacji właściciela — dopiero potem można ją wysłać.'}
                </div>
                {isOwner ? (
                  <button
                    onClick={() => void approve()}
                    disabled={busy}
                    className="text-sm bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 disabled:opacity-50"
                  >
                    Akceptuję rabat
                  </button>
                ) : q.status !== 'pending_approval' ? (
                  <button
                    onClick={() => void requestApproval()}
                    disabled={busy}
                    className="text-sm bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 disabled:opacity-50"
                  >
                    Wyślij do akceptacji
                  </button>
                ) : (
                  <div className="text-xs text-amber-700">Wysłano do akceptacji — czeka na właściciela.</div>
                )}
              </div>
            ) : (
              <button
                onClick={() => void issue()}
                disabled={busy || pusta}
                className="bg-brand-600 text-white px-5 py-2.5 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium"
                title={pusta ? 'Dodaj najpierw maszynę' : 'Nada numer i zamrozi ceny'}
              >
                {busy ? 'Wystawiam…' : 'Wystaw ofertę'}
              </button>
            )}
            <div className="text-[11px] text-slate-400 mt-1.5 max-w-xs">
              Wystawienie nada numer i zamrozi ceny. Potem oferty nie da się już zmienić.
            </div>
          </div>
        </div>

        {error && (
          <div role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Title: "na co jest oferta" ──────────────────────────────────────────────

/**
 * The document heading. It starts as the first machine's catalogue name
 * ("Rozrzutnik obornika") and the salesperson can overwrite it for this offer
 * ("Rozrzutnik kompostu") — the catalogue entry is untouched.
 */
function TitleField({ quote, onChanged }: { quote: Quote; onChanged: () => void }) {
  const [value, setValue] = useState(quote.title ?? '')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setValue(quote.title ?? ''), [quote.title])

  async function save(next: string) {
    if ((next.trim() || null) === (quote.title ?? null)) return
    setError(null)
    try {
      await api.patch(`/api/quotes/${quote.id}`, { title: next.trim() || null })
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się zapisać tytułu')
    }
  }

  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-lg p-3">
      <label className="text-xs">
        <span className="block text-slate-500 mb-0.5">Tytuł oferty — na co jest oferta</span>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void save(value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          placeholder={quote.proposedTitle ?? 'np. Rozrzutnik obornika'}
          maxLength={120}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600"
        />
      </label>
      <div className="text-[11px] text-slate-400 mt-1">
        {value.trim()
          ? `Na dokumencie: ${value.trim().toLocaleUpperCase('pl-PL')}`
          : quote.proposedTitle
            ? `Puste = z katalogu: ${quote.proposedTitle.toLocaleUpperCase('pl-PL')}. Wpisz własny, np. „Rozrzutnik kompostu".`
            : 'Wpisz, czego dotyczy oferta.'}
      </div>
      {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
    </div>
  )
}

// ── Machine search: type, Enter, done ───────────────────────────────────────

function MachineSearch({ quoteId, onAdded }: { quoteId: string; onAdded: () => void }) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<MachineHit[]>([])
  const [active, setActive] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([])
      return
    }
    const t = setTimeout(() => {
      api
        .get<{ machines: MachineHit[] }>(`/api/machines?search=${encodeURIComponent(q.trim())}`)
        .then((r) => {
          setHits(r.machines.filter((m) => m.status !== 'archiwalna').slice(0, 6))
          setActive(0)
        })
        .catch(() => setHits([]))
    }, 130)
    return () => clearTimeout(t)
  }, [q])

  async function add(machineId: string) {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/quotes/${quoteId}/items`, { machineId })
      setQ('')
      setHits([])
      onAdded()
      ref.current?.focus() // straight on to the next machine
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się dodać maszyny')
    } finally {
      setBusy(false)
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, hits.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && hits[active]) {
      e.preventDefault()
      void add(hits[active].id)
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <input
        ref={ref}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={busy}
        placeholder="Dodaj maszynę: model, kod katalogowy… (Enter dodaje)"
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600"
      />

      {hits.length > 0 && (
        <div className="mt-2 border border-slate-100 rounded overflow-hidden">
          {hits.map((m, i) => (
            <button
              key={m.id}
              onClick={() => void add(m.id)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-1.5 flex justify-between items-center gap-3 ${
                i === active ? 'bg-brand-50' : ''
              }`}
            >
              <span className="min-w-0">
                <span className="block text-sm text-slate-800 truncate">{m.model}</span>
                <span className="block text-xs text-slate-400">
                  {m.manufacturerName} · {m.catalogCode}
                </span>
              </span>
              <span className="text-sm tabular text-slate-600 shrink-0">{money(m.retailPrice, m.retailCurrency)}</span>
            </button>
          ))}
        </div>
      )}

      {error && <div className="mt-2 text-sm text-red-700">{error}</div>}
    </div>
  )
}

// ── One machine on the offer ────────────────────────────────────────────────

function ItemCard({
  item,
  isOwner,
  currency,
  onChanged,
}: {
  item: Item
  isOwner: boolean
  currency: string
  onChanged: () => void
}) {
  const [opts, setOpts] = useState<CatalogOption[] | null>(null)
  const [discount, setDiscount] = useState(String(Number(item.discountPct)))
  const [qty, setQty] = useState(String(item.quantity))
  const [error, setError] = useState<string | null>(null)
  // Every change is a PATCH + reload; `busy` keeps two quick clicks from
  // sending the same stale selection twice.
  const [busy, setBusy] = useState(false)

  const limit = Number(item.discountLimitPctAtIssue)
  const overLimit = Number(discount) > limit

  // A draft references the live catalogue. When the machine was replaced
  // there (a re-imported price list), the options can't be edited any more —
  // the line has to be re-added from the current catalogue.
  const [catalogGone, setCatalogGone] = useState(false)
  useEffect(() => {
    api
      .get<{ machine: { options: CatalogOption[] } }>(`/api/machines/${item.machineId}`)
      .then((r) => setOpts(r.machine.options))
      .catch((e) => {
        setOpts([])
        if (e instanceof ApiError && e.status === 404) setCatalogGone(true)
        else setError(e instanceof ApiError ? e.message : 'Nie udało się pobrać wyposażenia z katalogu')
      })
  }, [item.machineId])

  // Selection as stored on the line: catalogue option id → pieces.
  const qtyOf = new Map<string, number>()
  for (const o of item.options) if (o.optionId) qtyOf.set(o.optionId, o.quantity)
  const chosen = new Set(qtyOf.keys())
  // Priced on the line but gone from the catalogue since — the next change of
  // equipment drops them, so say so instead of letting money vanish silently.
  const znikniete = opts && !catalogGone ? item.options.filter((o) => !o.optionId || !opts.some((c) => c.id === o.optionId)) : []

  async function patch(body: Record<string, unknown>) {
    setError(null)
    setBusy(true)
    try {
      await api.patch(`/api/quote-items/${item.id}`, body)
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się zapisać')
    } finally {
      setBusy(false)
    }
  }

  function saveOptions(next: Set<string>, override?: { id: string; quantity: number }) {
    return patch({
      options: [...next].map((id) => ({ optionId: id, quantity: override?.id === id ? override.quantity : (qtyOf.get(id) ?? 1) })),
    })
  }

  async function toggleOption(optionId: string) {
    const next = new Set(chosen)
    if (next.has(optionId)) next.delete(optionId)
    else next.add(optionId)
    await saveOptions(next)
  }

  /** Pieces of an add-on (a double-sided Multiframe takes two heads). */
  async function setOptionQty(optionId: string, quantity: number) {
    const n = Math.min(MAX_OPTION_QTY, Math.max(1, quantity))
    if (n === (qtyOf.get(optionId) ?? 1)) return
    await saveOptions(chosen, { id: optionId, quantity: n })
  }

  /** In a choice group exactly one option is picked: choosing one drops its siblings. */
  async function pickInGroup(group: string, optionId: string) {
    const next = new Set(chosen)
    for (const o of opts ?? []) if (o.optionGroup === group) next.delete(o.id)
    next.add(optionId)
    await saveOptions(next)
  }

  // Groups in the order the catalogue lists them; add-ons after.
  const groups: string[] = []
  for (const o of opts ?? []) if (o.optionGroup && !groups.includes(o.optionGroup)) groups.push(o.optionGroup)
  const addons = (opts ?? []).filter((o) => !o.optionGroup)
  const plus = (o: CatalogOption) => (o.price && Number(o.price) > 0 ? `+ ${money(o.price, o.currency)}` : 'w cenie')

  async function remove() {
    try {
      await api.delete(`/api/quote-items/${item.id}`)
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się usunąć')
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-medium text-slate-800">{modelNaOfercie(item).model}</div>
          <div className="text-xs text-slate-400">
            {item.manufacturerName} · {modelNaOfercie(item).catalogCode}
          </div>
        </div>
        <button onClick={() => void remove()} className="text-xs text-slate-400 hover:text-red-600 shrink-0">
          usuń
        </button>
      </div>

      {catalogGone && (
        <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5">
          Ta maszyna została w międzyczasie zmieniona w katalogu — wyposażenia nie da się już edytować.
          Usuń pozycję i dodaj ją ponownie z aktualnego katalogu.
          {item.options.length > 0 && (
            <div className="mt-1 text-amber-700">
              Zapisane wyposażenie: {item.options.map((o) => (o.quantity > 1 ? `${o.name} × ${o.quantity}` : o.name)).join(', ')}
            </div>
          )}
        </div>
      )}

      {znikniete.length > 0 && (
        <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5">
          Wyposażenie usunięte w międzyczasie z katalogu: {znikniete.map((o) => o.name).join(', ')}. Jest jeszcze wliczone w cenę
          pozycji, ale zniknie przy następnej zmianie wyposażenia — sprawdź, czy ma zamiennik.
        </div>
      )}

      {/* 1. Configuration — one choice per group (base variant arrives pre-picked) */}
      {groups.map((g) => (
        <div key={g} className="mt-3">
          <div className="text-xs uppercase text-slate-400 mb-1.5">{g}</div>
          <div className="space-y-1">
            {(opts ?? []).filter((o) => o.optionGroup === g).map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                <input type="radio" name={`${item.id}-${g}`} checked={chosen.has(o.id)} disabled={busy} onChange={() => void pickInGroup(g, o.id)} />
                <span className="flex-1">{o.name}</span>
                <span className="tabular text-slate-500 text-xs">{plus(o)}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      {/* 2. Add-on equipment — any number, each in one or more pieces */}
      {addons.length > 0 && (
        <div className="mt-3">
          <div className="text-xs uppercase text-slate-400 mb-1.5">Wyposażenie dodatkowe</div>
          <div className="space-y-1">
            {addons.map((o) => {
              const on = chosen.has(o.id)
              const n = qtyOf.get(o.id) ?? 1
              // The stepper sits beside the label, not inside it — a button
              // inside <label> would also flip the checkbox.
              return (
                <div key={o.id} className="flex items-center gap-2 text-sm hover:bg-slate-50 px-1 py-0.5 rounded">
                  <label className="flex-1 flex items-center gap-2 cursor-pointer min-w-0">
                    <input type="checkbox" checked={on} disabled={busy} onChange={() => void toggleOption(o.id)} />
                    <span className="flex-1">{o.name}</span>
                  </label>
                  {on && (
                    <span className="flex items-center gap-1 text-xs" title="Ilość sztuk tego dodatku na jedną maszynę">
                      <button
                        type="button"
                        disabled={busy || n <= 1}
                        onClick={() => void setOptionQty(o.id, n - 1)}
                        className="w-5 h-5 border border-slate-300 rounded leading-none hover:border-brand-600 disabled:opacity-30"
                        aria-label="Mniej sztuk"
                      >
                        −
                      </button>
                      <span className="tabular w-4 text-center">{n}</span>
                      <button
                        type="button"
                        disabled={busy || n >= MAX_OPTION_QTY}
                        onClick={() => void setOptionQty(o.id, n + 1)}
                        className="w-5 h-5 border border-slate-300 rounded leading-none hover:border-brand-600 disabled:opacity-30"
                        aria-label="Więcej sztuk"
                      >
                        +
                      </button>
                    </span>
                  )}
                  <span className="tabular text-slate-500 text-xs">{n > 1 ? `${n} × ${money(o.price, o.currency)}` : money(o.price, o.currency)}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Numbers */}
      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <label className="text-xs" title="Ilość maszyn — mnoży całą pozycję razem z wyposażeniem">
          <span className="block text-slate-500 mb-0.5">Ilość maszyn</span>
          <input
            type="number"
            min="1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onBlur={() => Number(qty) !== item.quantity && void patch({ quantity: Number(qty) })}
            className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </label>

        <label className="text-xs">
          <span className="block text-slate-500 mb-0.5">
            Rabat % <span className="text-slate-400">(limit {percent(limit)})</span>
          </span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            onBlur={() => Number(discount) !== Number(item.discountPct) && void patch({ discountPct: Number(discount) })}
            className={`w-full px-2 py-1 border rounded text-sm ${
              overLimit ? 'border-amber-500 bg-amber-50' : 'border-slate-300'
            }`}
          />
        </label>

        <div className="text-xs">
          <span className="block text-slate-500 mb-0.5">Cena + wyposażenie</span>
          <div className="px-2 py-1 tabular text-slate-600">
            {money(Number(item.unitRetailPrice) + Number(item.optionsTotal), item.currency)}
          </div>
        </div>

        <div className="text-xs">
          <span className="block text-slate-500 mb-0.5">Cena dla klienta</span>
          <div className="px-2 py-1 tabular font-semibold text-slate-800">{money(item.unitNetPrice, item.currency)}</div>
          {/* An EUR line: the złoty the client will actually pay, at the offer's rate. */}
          {item.currency !== currency && (
            <div className="px-2 tabular text-slate-500">
              {item.lineNetPln != null ? `= ${money(item.lineNetPln, currency)}${item.quantity > 1 ? ` za ${item.quantity} szt.` : ''}` : 'brak kursu NBP'}
            </div>
          )}
        </div>

        {/* Owner-only. Sales never receives this field from the API. */}
        {isOwner && (
          <div className="text-xs">
            <span className="block text-slate-500 mb-0.5">Marża</span>
            <div className="px-2 py-1 tabular">
              {item.marginPln ? (
                <span className={Number(item.marginPln) > 0 ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
                  {money(item.marginPln, currency)}
                </span>
              ) : (
                <span className="text-slate-400" title="Brak ceny zakupu albo kursu — marża nieznana">
                  —
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {overLimit && (
        <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          Rabat {percent(discount)} przekracza limit producenta ({percent(limit)}).{' '}
          {isOwner ? 'Zatwierdzisz go jednym kliknięciem przed wystawieniem.' : 'Ta oferta będzie wymagać akceptacji właściciela.'}
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
    </div>
  )
}

function Row({ label, value, strong, tone }: { label: string; value: string; strong?: boolean; tone?: 'green' | undefined }) {
  return (
    <div className="flex justify-between gap-8">
      <span className="text-slate-500">{label}</span>
      <span
        className={`tabular ${strong ? 'font-semibold text-slate-800' : 'text-slate-700'} ${
          tone === 'green' ? 'text-green-700' : ''
        }`}
      >
        {value}
      </span>
    </div>
  )
}
